import { useCallback, useEffect, useState } from 'react'

/**
 * A setting that belongs to this person, on this browser.
 *
 * Some choices are about the dashboard and some are about the viewing. Which
 * cars a stint chart shows is the second kind: two people watching one screen
 * want different rows, and storing it server-side would mean either of them
 * silently changing the other's view. It also differs between the laptop on the
 * pit wall and the phone in the garage, which is exactly what per-browser
 * storage is.
 *
 * Every access is guarded. Private windows, cleared site data and browsers set
 * to block storage all throw here rather than returning nothing, and none of
 * them is a reason for a screen not to render — it just forgets between visits.
 */
export function useLocalSetting<T>(
  key: string,
  fallback: T,
  parse: (raw: unknown) => T,
): [T, (next: T) => void] {
  const [value, setValue] = useState<T>(() => read(key, fallback, parse))

  // The key changes when the screen or the dashboard does, and the value has to
  // follow it — otherwise switching between two stint screens shows the first
  // one's filter under the second one's name.
  useEffect(() => {
    setValue(read(key, fallback, parse))
    // fallback and parse are recomputed by the caller on every render; keying
    // the effect on them would reset the setting continuously.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key])

  const set = useCallback(
    (next: T) => {
      setValue(next)
      try {
        localStorage.setItem(key, JSON.stringify(next))
      } catch {
        // Held for this visit and forgotten after it, which is the right
        // degradation: the alternative is refusing to change the view at all.
      }
    },
    [key],
  )

  return [value, set]
}

function read<T>(key: string, fallback: T, parse: (raw: unknown) => T): T {
  try {
    const raw = localStorage.getItem(key)
    if (raw == null) return fallback
    return parse(JSON.parse(raw))
  } catch {
    return fallback
  }
}
